import type { ProjectTask } from '@/types/odoo';

/**
 * Interventie photo keys, read side. The API writes new photos under
 * `{project}/interventie/fase-{faseId}/{yyyymmdd-…}-{file}`; older photos sit in
 * `…/interventie/interventies/` (the dashboard pile) or `…/interventie/{taskName}/`.
 * The shape is owned by portal-api/src/utils/interventie-photo-keys.ts — this only
 * reads the fase id back out of a key, never builds one.
 */

/** The fase id of an id-keyed interventie photo, or null for an older key. */
export function interventieFaseIdFromKey(key: string): number | null {
  const parts = key.split('/');
  // …/interventie/fase-{id}/{file} — the folder is the second-to-last segment,
  // so a project name that itself contains "/interventie/" cannot confuse it.
  if (parts.length < 4 || parts[parts.length - 3] !== 'interventie') return null;
  const m = /^fase-(\d+)$/.exec(parts[parts.length - 2]);
  return m ? Number(m[1]) : null;
}

/**
 * Split a project's interventie photos into one list per TAGGED fase and the
 * rest ("Oudere interventiefoto's"): the legacy pile, the name-keyed folders and
 * any id-keyed folder of a fase that is not (or no longer) tagged. No photo is
 * ever dropped — every input lands in exactly one bucket.
 */
export function splitInterventiePhotos<P extends { key: string }>(
  photos: P[],
  taggedFaseIds: Iterable<number>,
): { byFase: Map<number, P[]>; older: P[] } {
  const byFase = new Map<number, P[]>();
  for (const id of taggedFaseIds) byFase.set(id, []);
  const older: P[] = [];
  for (const p of photos) {
    const faseId = interventieFaseIdFromKey(p.key);
    const bucket = faseId !== null ? byFase.get(faseId) : undefined;
    if (bucket) bucket.push(p);
    else older.push(p);
  }
  return { byFase, older };
}

/**
 * The fases that hold an interventie: a tagged fase, or the parent of a tagged
 * day. Same resolution as the upload folder (a day uploads into its fase), so a
 * fase marked here is exactly a fase whose id-keyed photos get their own block.
 */
export function interventieFaseIds(tasks: ProjectTask[]): Set<number> {
  const ids = new Set<number>();
  for (const t of tasks) {
    if (!t.isInterventie) continue;
    ids.add(Array.isArray(t.parent_id) ? t.parent_id[0] : t.id);
  }
  return ids;
}

/** "1 foto" / "N foto's" — the count shown next to an interventie date-group. */
export function fotoCount(n: number): string {
  return n === 1 ? '1 foto' : `${n} foto's`;
}
